import type { TFunction } from 'i18next'
import { TBatchImportLogResult, EBatchImportResultAction } from './batch-import.type'
import {
  EBatchImportErrorCode,
  EBatchImportTier,
  getBatchImportErrorCode,
  getBatchImportTier,
  parseBatchImportErrors,
} from './batch-import-errors.util'

export type TBatchImportRowReport = {
  // undefined while the line is still pending
  tier?: EBatchImportTier
  statusLabel: string
  details: { text: string; rawMessage?: string }[]
}

const getStatusLabel = (t: TFunction, tier?: EBatchImportTier): string => {
  const statusLabelByTier: Record<EBatchImportTier, string> = {
    [EBatchImportTier.NOMINAL]: t('components.file-uploader.batch-import-success'),
    [EBatchImportTier.WARNING]: t('components.file-uploader.batch-import-warning'),
    [EBatchImportTier.ERROR]: t('components.file-uploader.batch-import-error'),
  }
  return tier ? statusLabelByTier[tier] : t('components.file-uploader.batch-import-pending')
}

// Single source of truth for what a line says, shared by the on-screen table and the CSV export
export const getBatchImportRowReport = (
  t: TFunction,
  result?: TBatchImportLogResult,
): TBatchImportRowReport => {
  if (!result) return { statusLabel: getStatusLabel(t), details: [] }

  const tier = getBatchImportTier(result)
  const statusLabel = getStatusLabel(t, tier)

  if (tier === EBatchImportTier.NOMINAL) {
    const successLabelByAction: Record<EBatchImportResultAction, string> = {
      [EBatchImportResultAction.CREATED]: t('components.file-uploader.batch-import-created'),
      [EBatchImportResultAction.UPDATED]: t('components.file-uploader.batch-import-updated'),
      [EBatchImportResultAction.REMOVED]: t('components.file-uploader.batch-import-removed'),
    }
    return {
      tier,
      statusLabel,
      details: [
        {
          text: result.resultAction
            ? successLabelByAction[result.resultAction]
            : t('components.file-uploader.batch-import-processed'),
        },
      ],
    }
  }

  const errorLabelByCode: Record<EBatchImportErrorCode, string> = {
    [EBatchImportErrorCode.PATIENT_MISSING_IDENTITY]: t(
      'components.file-uploader.batch-import-errors.patient-missing-identity',
    ),
    [EBatchImportErrorCode.PATIENT_NOT_FOUND]: t(
      'components.file-uploader.batch-import-errors.patient-not-found',
    ),
    [EBatchImportErrorCode.PATIENT_DATA_MISSING]: t(
      'components.file-uploader.batch-import-errors.patient-data-missing',
    ),
    [EBatchImportErrorCode.MEDEVENT_NO_FOLLOWUP]: t('components.file-uploader.batch-import-no-followup'),
    [EBatchImportErrorCode.MEDEVENT_FOLLOWUP_ENDED]: t(
      'components.file-uploader.batch-import-errors.med-event-followup-ended',
    ),
    [EBatchImportErrorCode.MEDEVENT_MARKED_FOR_DELETION]: t(
      'components.file-uploader.batch-import-errors.med-event-marked-for-deletion',
    ),
    [EBatchImportErrorCode.MEDEVENT_RESTORE_DIFFERENT_REMOTE_ID]: t(
      'components.file-uploader.batch-import-errors.med-event-restore-different-remote-id',
    ),
    [EBatchImportErrorCode.MEDEVENT_INVALID_DATE]: t(
      'components.file-uploader.batch-import-errors.med-event-invalid-date',
    ),
    [EBatchImportErrorCode.MEDEVENT_DIFFERENT_MED_EVENT_ID]: t(
      'components.file-uploader.batch-import-errors.med-event-different-med-event-id',
    ),
  }

  const errors = parseBatchImportErrors(result.errors).filter((error) => !!error.message)
  return {
    tier,
    statusLabel,
    details:
      errors.length > 0
        ? errors.map((error) => {
            const code = getBatchImportErrorCode(error)
            return { text: code ? errorLabelByCode[code] : (error.message ?? ''), rawMessage: error.message }
          })
        : [{ text: t('components.file-uploader.batch-import-unknown-error') }],
  }
}
